'use client'

import { useEffect, useState, createContext } from 'react'
import { Auth0Provider } from '@auth0/nextjs-auth0'
import { Provider as JotaiProvider } from 'jotai'
import ConvertSDK, { SystemEvents } from '@convertcom/js-sdk'
import { sendGTMEvent } from '@next/third-parties/google'

import { User } from '@auth0/nextjs-auth0/types'

type ProvidersProps = {
  children: React.ReactNode
  user?: User
}

type ConvertSdkContext = ReturnType<ConvertSDK['createContext']>

type ConvertContextValue = {
  sdkContext?: ConvertSdkContext
  isConvertReady: boolean
}

type ConvertBucketingEvent = {
  experienceKey: string
  variationKey: string
}

type ConvertBucketingPayload = {
  experienceKey?: unknown
  variationKey?: unknown
}

export const ConvertContext = createContext<ConvertContextValue>({ isConvertReady: false })

const convertSdk = new ConvertSDK({
  sdkKey: '535CD2311174C1AD2023BC709CFD09A2DA6FC93A9AEE9BF8585A1937020A2C2F',
  dataRefreshInterval: 30000, // 30 seconds
  environment: 'production',
  logger: {
    logLevel: 5, // LogLevel.SILENT
  },
})

// Tracks the latest known variation per experience key across bucketing callbacks.
const activeExperiencesByKey = new Map<string, string>()

// Snapshot of the last GTM payload so we avoid duplicate pushes.
let lastExperiencesPayload = ''

// Persist a stable visitor ID locally so Convert bucketing stays consistent between visits.
function getOrCreateVisitorId() {
  const storageKey = 'convert_visitor_id'
  const existingVisitorId = window.localStorage.getItem(storageKey)

  if (existingVisitorId) {
    return existingVisitorId
  }

  const newVisitorId = crypto.randomUUID()
  window.localStorage.setItem(storageKey, newVisitorId)
  return newVisitorId
}

// Build Convert audience variables from Auth0 metadata with safe defaults.
function getAudienceVars(user?: User) {
  const {
    user_type: userType = 'anonymous',
    company_name: companyName = '',
    company_sectors: companySectors = [],
  } = user?.meta || {}

  return {
    user_type: userType,
    company_name: companyName,
    company_sectors: companySectors,
  }
}

// Convert SDK emits `unknown`; normalize to the specific shape we care about.
function parseBucketingEvent(data: unknown): ConvertBucketingEvent | null {
  const bucketingData = (data ?? {}) as ConvertBucketingPayload

  const experienceKey =
    typeof bucketingData.experienceKey === 'string' ? bucketingData.experienceKey : null
  const variationKey =
    typeof bucketingData.variationKey === 'string' ? bucketingData.variationKey : null

  if (!experienceKey || !variationKey) {
    return null
  }

  return {
    experienceKey,
    variationKey,
  }
}

// Convert map state into the array structure we send to GTM.
function getActiveExperiences(): ConvertBucketingEvent[] {
  return Array.from(activeExperiencesByKey).map(([key, variation]) => ({
    experienceKey: key,
    variationKey: variation,
  }))
}

function pushBucketingEventToGTM(activeExperiences: ConvertBucketingEvent[]) {
  const experiencesPayload = JSON.stringify(activeExperiences)

  // Ignore repeated payloads to keep GTM event volume clean.
  if (experiencesPayload === lastExperiencesPayload) {
    return
  }

  lastExperiencesPayload = experiencesPayload

  sendGTMEvent({
    event: 'activeExperiences',
    activeExperiences,
  })
}

// Register once at module scope so all provider instances share a single listener.
convertSdk.on(SystemEvents.BUCKETING, (data: unknown) => {
  const parsedEvent = parseBucketingEvent(data)

  if (!parsedEvent) {
    return
  }

  activeExperiencesByKey.set(parsedEvent.experienceKey, parsedEvent.variationKey)
  pushBucketingEventToGTM(getActiveExperiences())
})

convertSdk.on(SystemEvents.CONVERSION, (data: unknown) => {
  console.info('Convert conversion event', data)
})

export default function Providers({ children, user }: ProvidersProps) {
  const [sdkContext, setSdkContext] = useState<ConvertSdkContext>()
  const [isConvertReady, setIsConvertReady] = useState(false)

  useEffect(() => {
    setIsConvertReady(false)

    const initConvert = async () => {
      try {
        // Check if SDK is accessible (may be blocked by ad blockers)
        if (!convertSdk || typeof convertSdk.onReady !== 'function') {
          console.warn('Convert SDK is not available (possibly blocked by ad blocker)')
          setIsConvertReady(true)
          return
        }

        await convertSdk.onReady()

        // Keep visitor bucketing stable across sessions for the same browser.
        const visitorId = getOrCreateVisitorId()
        const audienceVars = getAudienceVars(user)

        const context = convertSdk.createContext(visitorId, audienceVars)

        setSdkContext(context)
        setIsConvertReady(true)
      } catch (error) {
        console.warn('Convert SDK failed to initialize (possibly blocked by ad blocker):', error)
        setIsConvertReady(true)
      }
    }

    initConvert()
  }, [user])

  return (
    <Auth0Provider user={user}>
      <ConvertContext.Provider value={{ sdkContext, isConvertReady }}>
        <JotaiProvider>{children}</JotaiProvider>
      </ConvertContext.Provider>
    </Auth0Provider>
  )
}

export { convertSdk }
